import { useMenu, useClearRefinements } from 'react-instantsearch';

import { delimitWithCommas } from '@javascript/lib/utils';
import classNames from 'classnames';

type ListItemProps = {
  children: React.ReactNode;
  isSelected: boolean;
};

interface FilterOptionProps extends Omit<ListItemProps, 'children'> {
  label: string;
  onChange: VoidFunction;
  children?: React.ReactNode;
  count?: number;
}

const ListItem = ({ children, isSelected }: ListItemProps) => {
  return (
    <li
      className={classNames(
        'ac-instant-search__filter-option ch-form__group ch-pa--2 ch-rounded--sm ch-bg--white',
        isSelected ? 'ch-bc--ac-blue ch-ba--3' : `ch-bc--grey-4 ch-ba--1`
      )}
    >
      {children}
    </li>
  );
};

const CheckBox = ({
  id,
  children,
  count,
  isSelected,
  label,
  onChange,
}: FilterOptionProps & {
  id: string;
}) => {
  return (
    <ListItem isSelected={isSelected}>
      <input
        checked={isSelected}
        className="ch-checkbox"
        id={`checkbox-${id}`}
        onChange={onChange}
        type="checkbox"
      />
      <label
        className={classNames(
          'ch-checkbox__label ch-checkbox__label--primary ch-checkbox__label--right ch-display--flex',
          count === undefined && 'ch-justify-content--between'
        )}
        htmlFor={`checkbox-${id}`}
      >
        {children}
        {label}
        {count !== undefined && (
          <span className="ch-fs--3 ch-fw--400 ch-ml--auto">
            {delimitWithCommas(count)}
          </span>
        )}
      </label>
    </ListItem>
  );
};

const Radio = ({
  attribute,
  children,
  count,
  isSelected,
  label,
  onChange,
  suffix,
}: FilterOptionProps & {
  attribute: string;
  suffix?: string;
}) => {
  return (
    <ListItem isSelected={isSelected}>
      <label className="ch-display--flex ch-align-items--center ch-width--12 ch-fs--3 ch-hand">
        {children}
        {label}
        {suffix && <span className="ch-ml--1 ch-fw--400">{suffix}</span>}
        {count && (
          <span className="ch-fw--400 ch-ml--auto">
            {delimitWithCommas(count)}
          </span>
        )}
        <input
          checked={isSelected}
          className={classNames(
            'ch-ml--2 ac-instant-search__single-select-radio',
            !count && 'ch-ml--auto'
          )}
          name={attribute}
          onChange={onChange}
          type="radio"
        />
      </label>
    </ListItem>
  );
};

const Any = ({ attribute }: { attribute: string }) => {
  const { items } = useMenu({ attribute });
  const { refine } = useClearRefinements({ includedAttributes: [attribute] });

  const optionsSelected = items.some(
    (item: { isRefined: boolean }) => item.isRefined
  );

  return (
    <Radio
      attribute={attribute}
      isSelected={!optionsSelected}
      label="Any"
      onChange={() => refine()}
    />
  );
};

const InstantSearchFilterOption = {
  Any,
  CheckBox,
  Radio,
};

export default InstantSearchFilterOption;
